import { CustomTag } from "sipli-fleet-core/components/CustomSharedTag";
import { App, Button, Dropdown } from "antd";
import type { TableProps } from "antd";
import {
  useCallback,
  useMemo,
  useState,
  type ComponentType,
  type Dispatch,
  type MouseEvent,
  type SetStateAction,
} from "react";
import { useTranslation } from "react-i18next";
import {
  CrossIcon,
  DeleteIcon,
  EditIcon,
  MenuIcon,
  SearchIcon,
} from "src/assets/svgs";
import {
  CustomButton,
  CustomTable,
  CustomTooltip,
  CustomTypography,
} from "sipli-fleet-core/components";
import CustomSharedConfirmationModal from "sipli-fleet-core/components/CustomSharedConfirmationModal/CustomSharedConfirmationModal";
import CustomSharedInput from "sipli-fleet-core/components/CustomSharedInput/CustomSharedInput";
import {
  useMutationDeleteModelProperty,
  useMutationUpdateModelProperty,
} from "src/queries/administrator/modelProperty.queries";
import type { ModelPropertyRecord } from "src/services/administrator/modelPropertyService";
import { hasAdministrationPermission } from "src/util/administrationPermission";

interface AddModelPropertiesTableProps {
  data: ModelPropertyRecord[];
  isLoading?: boolean;
  pagination: { page: number; size: number; total?: number };
  setPagination: Dispatch<SetStateAction<{ page: number; size: number }>>;
  sorting: { sortBy: string | undefined; sortDir: "ASC" | "DESC" | undefined };
  setSorting: Dispatch<
    SetStateAction<{ sortBy: string | undefined; sortDir: "ASC" | "DESC" | undefined }>
  >;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  onEdit: (record: ModelPropertyRecord) => void;
}

const CrossIconComponent = CrossIcon as unknown as ComponentType<{ className?: string }>;
const DeleteIconComponent = DeleteIcon as unknown as ComponentType<{ className?: string }>;
const EditIconComponent = EditIcon as unknown as ComponentType<{ className?: string }>;
const MenuIconComponent = MenuIcon as unknown as ComponentType<{ className?: string }>;
const SearchIconComponent = SearchIcon as unknown as ComponentType<{ className?: string }>;
const { CustomText } = CustomTypography;

const dataIndexToColumnId: Record<string, string> = {
  model: "MODEL",
  property: "PROPERTY",
  status: "STATUS",
};

const isActive = (value: unknown) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") return ["active", "true", "1"].includes(value.toLowerCase());
  return false;
};

const getName = (value: unknown) => {
  if (!value || typeof value !== "object") return "--";
  const item = value as {
    name?: string;
    code?: string;
    propertyNameComm?: string;
    propertyNameInternal?: string;
  };
  return item.propertyNameComm ?? item.name ?? item.propertyNameInternal ?? item.code ?? "--";
};

const getUnitBrand = (record: ModelPropertyRecord) => record.make ?? record.model?.make;

const StatusBadge = ({ active }: { active?: unknown }) => {
  const { t } = useTranslation();
  const activeStatus = isActive(active);
  return (
    <CustomTag
      className={
        activeStatus
          ? "bg-green-50 text-green-600"
          : "bg-gray-50 text-gray-400"
      }
    >
      {activeStatus ? t("active") : t("inactive")}
    </CustomTag>
  );
};

const AddModelPropertiesTable = ({
  data,
  isLoading = false,
  pagination,
  setPagination,
  sorting,
  setSorting,
  search,
  setSearch,
  onEdit,
}: AddModelPropertiesTableProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [deleteRecord, setDeleteRecord] = useState<ModelPropertyRecord | null>(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | number | null>(null);
  const [openActionId, setOpenActionId] = useState<string | number | null>(null);
  const canEdit = hasAdministrationPermission("edit");
  const canDelete = hasAdministrationPermission("delete");
  const updateMutation = useMutationUpdateModelProperty();
  const deleteMutation = useMutationDeleteModelProperty();

  const onTableChange: TableProps<ModelPropertyRecord>["onChange"] = (
    _pagination,
    _filters,
    sorter,
    extra,
  ) => {
    if (extra.action === "filter") return;
    const normalizedSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const order = normalizedSorter?.order;
    const dataIndex = normalizedSorter?.field || normalizedSorter?.columnKey;
    if (!order || !dataIndex) {
      setSorting({ sortBy: "ID", sortDir: "DESC" });
      return;
    }
    setSorting({
      sortBy: dataIndexToColumnId[String(dataIndex)] ?? "ID",
      sortDir: order === "ascend" ? "ASC" : "DESC",
    });
    setPagination((previous) => ({ ...previous, page: 0 }));
  };

  const handleStatusChange = useCallback(
    async (record: ModelPropertyRecord) => {
      setStatusUpdatingId(record.id);
      try {
        await updateMutation.mutateAsync({
          id: record.id,
          payload: { active: !isActive(record.active) },
        });
        setOpenActionId(null);
        message.success(t("model_property_status_updated"));
      } catch {
        message.error(t("setup_status_update_failed"));
      } finally {
        setStatusUpdatingId(null);
      }
    },
    [message, t, updateMutation],
  );

  const handleDeleteConfirm = async () => {
    if (!deleteRecord) return;
    try {
      await deleteMutation.mutateAsync(deleteRecord.id);
      message.success(t("model_property_deleted"));
      setDeleteRecord(null);
    } catch {
      message.error(t("cannot_delete_item"));
    }
  };

  const renderActions = useCallback(
    (record: ModelPropertyRecord) => {
      const isCurrentStatusLoading = updateMutation.isPending && statusUpdatingId === record.id;
      const disabledActionClass =
        "!cursor-not-allowed !opacity-90 hover:!bg-transparent active:!bg-transparent focus:!bg-transparent !bg-transparent";
      const handleStatusClick = async (event: MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        await handleStatusChange(record);
      };

      return (
        <div className="flex items-center justify-center gap-2 customer-actions">
          <CustomTooltip title={!canEdit ? t("permission_denied") : t("edit")}>
            <span className="inline-flex">
              <CustomButton
                type="icon"
                buttonSize="small"
                aria-label={t("edit_add_model_properties")}
                disabled={!canEdit}
                className={!canEdit ? disabledActionClass : ""}
                icon={
                  <EditIconComponent
                    className={`w-[16px] h-[16px] ${!canEdit ? "opacity-30 grayscale" : ""}`}
                  />
                }
                onClick={(event) => {
                  event?.stopPropagation?.();
                  onEdit(record);
                }}
              />
            </span>
          </CustomTooltip>
          <CustomTooltip title={!canDelete ? t("permission_denied") : t("delete")}>
            <span className="inline-flex">
              <CustomButton
                type="icon"
                buttonSize="small"
                aria-label={t("delete_add_model_properties")}
                disabled={!canDelete}
                className={!canDelete ? disabledActionClass : ""}
                icon={
                  <DeleteIconComponent
                    className={`w-[15px] h-[16px] ${!canDelete ? "opacity-30 grayscale" : ""}`}
                  />
                }
                onClick={(event) => {
                  event?.stopPropagation?.();
                  setDeleteRecord(record);
                }}
              />
            </span>
          </CustomTooltip>
          <CustomTooltip title={!canEdit ? t("permission_denied") : t("more_action")}>
            <Dropdown
              disabled={!canEdit}
              trigger={["click"]}
              open={openActionId === record.id}
              onOpenChange={(open) => setOpenActionId(open ? record.id : null)}
              dropdownRender={() => (
                <div
                  className="min-w-[180px] overflow-hidden rounded-sm bg-white shadow-[0_6px_16px_rgba(0,0,0,0.16)]"
                  onClick={(event) => event.stopPropagation()}
                  onMouseDown={(event) => event.stopPropagation()}
                >
                  <Button
                    type="text"
                    loading={isCurrentStatusLoading}
                    disabled={updateMutation.isPending && !isCurrentStatusLoading}
                    className="!flex !h-[36px] !w-full !cursor-pointer !items-center !justify-start !rounded-none !border-0 !bg-white !px-4 !text-left !text-[14px] !text-black hover:!bg-primary6 focus:!bg-white active:!bg-white focus:!outline-none active:!outline-none"
                    onClick={handleStatusClick}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                  >
                    {isActive(record.active) ? t("inactive") : t("active")}
                  </Button>
                </div>
              )}
              placement="bottomRight"
              overlayClassName="customer-action-dropdown"
            >
              <span className="inline-flex">
                <CustomButton
                  type="icon"
                  buttonSize="small"
                  aria-label={t("more_actions")}
                  disabled={!canEdit}
                  icon={
                    <MenuIconComponent
                      className={`w-[3px] h-[16px] ${!canEdit ? "opacity-30 grayscale" : ""}`}
                    />
                  }
                  className={`ml-2 !bg-transparent active:!bg-transparent focus:!bg-transparent ${
                    !canEdit ? disabledActionClass : ""
                  }`}
                />
              </span>
            </Dropdown>
          </CustomTooltip>
        </div>
      );
    },
    [canDelete, canEdit, handleStatusChange, onEdit, openActionId, statusUpdatingId, t, updateMutation.isPending],
  );

  const columns: TableProps<ModelPropertyRecord>["columns"] = useMemo(
    () => [
      {
        title: t("unit_make"),
        dataIndex: "make",
        key: "make",
        render: (_value, record) => (
          <CustomText className="!text-sm !text-black">{getName(getUnitBrand(record))}</CustomText>
        ),
      },
      {
        title: t("unit_model"),
        dataIndex: "model",
        key: "model",
        sorter: true,
        render: (value) => <CustomText className="!text-sm !text-black">{getName(value)}</CustomText>,
      },
      {
        title: t("property"),
        dataIndex: "property",
        key: "property",
        sorter: true,
        render: (value) => <CustomText className="!text-sm !text-black">{getName(value)}</CustomText>,
      },
      {
        title: t("status"),
        dataIndex: "status",
        key: "status",
        sorter: true,
        render: (_value, record) => <StatusBadge active={record.active} />,
      },
      {
        title: t("actions"),
        key: "actions",
        align: "center",
        width: 120,
        render: (_value, record) => renderActions(record),
      },
    ],
    [renderActions, t],
  );

  return (
    <>
      <div className="flex items-center justify-between gap-3 px-[18px] -mt-2 mb-4">
        <CustomSharedInput
          allowClear={{
            clearIcon: (
              <CrossIconComponent className="mr-0.5 customers-search-clear-icon w-[8.5px] h-[8.5px]" />
            ),
          }}
          prefix={<SearchIconComponent className="customers-search-icon w-[14px] h-[14px]" />}
          placeholder={t("search")}
          value={search}
          className="global-filter-search-class !rounded-[4px]"
          onChange={(event) => {
            setSearch(event.target.value);
            setPagination((previous) => ({ ...previous, page: 0 }));
          }}
        />
      </div>

      <CustomTable<ModelPropertyRecord>
        customClass="customers-table"
        data={data}
        columns={columns}
        rowKey={(record) => String(record.id)}
        isLoading={isLoading}
        pageSize={pagination.size}
        currentPage={pagination.page}
        totalCount={pagination.total ?? data.length}
        handlePagination={(page) => setPagination((previous) => ({ ...previous, page }))}
        handlePageSizeChange={(size) => setPagination({ page: 0, size: Number(size) })}
        showSearchInput={false}
        showCustomizeColumns={false}
        showDownload={false}
        sorting={sorting}
        onChange={onTableChange}
        locale={{ emptyText: t("no_data") }}
      />

      <CustomSharedConfirmationModal
        open={Boolean(deleteRecord)}
        title={t("delete_add_model_properties")}
        description={t("delete_add_model_properties_confirmation")}
        confirmText={t("delete")}
        cancelText={t("cancel")}
        isLoading={deleteMutation.isPending}
        onCancel={() => setDeleteRecord(null)}
        onConfirm={handleDeleteConfirm}
        centered
      />
    </>
  );
};

export default AddModelPropertiesTable;
